import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { slotTerminalType, terminalBoardPinNumMax } from '@models/system-setting';
import { TranslateService } from '@ngx-translate/core';
import {
    doRelayTitles,
    terminalOptions,
    terminalTitles,
    TerminalOption,
} from '@components/system-setting/slot-dialog/constant';
import {
    normalPinNum,
    relayPinNum,
    SlotOptionType,
    Terminal,
    TerminalSide,
    TerminalType,
    terminalType,
} from 'icreate/common/models/slot';

@Component({
    selector: 'app-slot-dialog',
    templateUrl: './slot-dialog.component.html',
    styleUrls: ['./slot-dialog.component.css'],
    standalone: false,
})
export class SlotDialogComponent implements OnInit {
    readonly PIN_MAX = terminalBoardPinNumMax;
    readonly RIGHT_TERMINAL = TerminalSide.RIGHT;
    readonly LEFT_TERMINAL = TerminalSide.LEFT;
    readonly NORMAL_PIN_NUM = normalPinNum;
    readonly RELAY_PIN_NUM = relayPinNum;

    remainingTerminals: number = 64;
    slotType: SlotOptionType;
    terminalOptions = terminalOptions;
    terminalTitles = terminalTitles;
    selectedTerminals: Terminal[] = [];

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: {
            title: string;
            ioType: SlotOptionType;
            terminal: Terminal[];
        },
        private dialogRef: MatDialogRef<SlotDialogComponent>,
        private cdr: ChangeDetectorRef,
        private translate: TranslateService
    ) {}

    ngAfterViewInit(): void {
        this.cdr.detectChanges();
    }

    ngOnInit() {
        this.slotType = this.data.ioType;
        this.selectedTerminals = [...this.data.terminal];
        this.updateRemainingTerminals();
    }

    onTerminalClick = (option: TerminalOption, side: TerminalSide) => {
        switch (option.type) {
            case terminalType.normal:
                this.addNormalTerminal(side);
                break;

            case terminalType.relay:
                this.addRelayTerminal(side, option.doRelayType);
                break;
        }
    };

    isTerminalDisabled(optionType: TerminalType) {
        if (!this.isDOType) return false;

        if (optionType === terminalType.normal) {
            return this.remainingTerminals < normalPinNum;
        }

        return this.remainingTerminals < relayPinNum;
    }

    getTerminalTitle(terminal: Terminal): string {
        if (terminal.type === terminalType.relay && terminal.doRelayType) {
            return doRelayTitles[terminal.doRelayType];
        }

        return terminalTitles[terminal.type];
    }

    addNormalTerminal(side: TerminalSide, isSecond: boolean = false) {
        if (this.isDOType) {
            if (this.remainingTerminals == 0 || this.isTerminalDisabled(terminalType.normal)) return;

            this.selectedTerminals.push({
                index: this.selectedTerminals.length + 1,
                type: slotTerminalType.normal,
                num: this.NORMAL_PIN_NUM,
                side: side,
            });

            this.updateRemainingTerminals();
        }

        if (this.isDIType) {
            const selectedIndex = this.selectedTerminals.findIndex(terminal => {
                if (terminal.index == 0 && isSecond) return false;
                else if (terminal.index == 1 && !isSecond) return false;
                return true;
            });

            const newTerminal = {
                index: isSecond ? 1 : 0,
                type: slotTerminalType.normal,
                num: this.NORMAL_PIN_NUM,
                side: side,
            };

            if (selectedIndex < 0) {
                this.selectedTerminals.push(newTerminal);
            } else {
                this.selectedTerminals.splice(selectedIndex, 1, newTerminal);
            }
        }

        if (this.isAOType || this.isAIType) {
            const newTerminal = {
                index: 0,
                type: slotTerminalType.normal,
                num: this.NORMAL_PIN_NUM,
                side: side,
            };

            if (this.selectedTerminals.length > 0) {
                this.selectedTerminals.splice(0, 1, newTerminal);
            } else {
                this.selectedTerminals.push(newTerminal);
            }
        }
    }

    addRelayTerminal(side: TerminalSide, doRelayType?: TerminalOption['doRelayType']) {
        if (!this.isDOType) return;
        if (this.remainingTerminals == 0 || this.isTerminalDisabled(terminalType.relay)) return;

        this.selectedTerminals.push({
            index: this.selectedTerminals.length + 1,
            type: slotTerminalType.relay,
            num: this.RELAY_PIN_NUM,
            side: side,
            doRelayType: doRelayType,
        });

        this.updateRemainingTerminals();
    }

    deleteTerminal(index: number) {
        let indexToDelete = this.selectedTerminals.findIndex(terminal => terminal.index === index);

        if (indexToDelete !== -1) {
            this.selectedTerminals.splice(indexToDelete, 1);
            this.selectedTerminals = this.selectedTerminals.map((terminal, index) => ({
                ...terminal,
                index: index + 1,
            }));
        }

        this.updateRemainingTerminals();
    }

    save() {
        this.dialogRef.close({
            terminal: this.selectedTerminals,
            title: this.data.title,
            type: this.data.ioType,
        });
    }

    close() {
        this.dialogRef.close(false);
    }

    getTerminalType(terminal: Terminal) {
        const direction = terminal.side === TerminalSide.RIGHT ? 'right' : 'left';

        return {
            key: `system-settings.do-slot.terminal.${direction}`,
            params: { num: terminal.num },
        };
    }

    isTerminalSelected(side: TerminalSide, isSecond: boolean = false): boolean {
        if (this.isDIType) {
            const isSelected = this.selectedTerminals.find(terminal => {
                if (terminal.side !== side) return false;
                if (terminal.index == 0 && isSecond) return false;
                else if (terminal.index == 1 && !isSecond) return false;
                return true;
            });
            return !!isSelected;
        } else if (this.isAOType || this.isAIType) {
            const isSelected = this.selectedTerminals.find(terminal => terminal.side === side);
            return !!isSelected;
        }
        return false;
    }

    updateRemainingTerminals() {
        if (!this.isDOType) return;
        let totalNum = terminalBoardPinNumMax;

        this.selectedTerminals.forEach(terminal => {
            totalNum -= terminal.num;
        });
        this.remainingTerminals = totalNum;
    }

    get convertPinToPercent(): number {
        return (100 / terminalBoardPinNumMax) * (terminalBoardPinNumMax - this.remainingTerminals);
    }

    get isSaveDisabled() {
        if (this.isDOType) {
            return this.remainingTerminals == terminalBoardPinNumMax || this.remainingTerminals < 0;
        } else if (this.isDIType) {
            return this.selectedTerminals.length != 2;
        } else if (this.isAOType || this.isAIType) {
            return this.selectedTerminals.length != 1;
        }
    }

    get isDOType() {
        return this.slotType == 'DO';
    }

    get isDIType() {
        return this.slotType == 'DI';
    }

    get isAOType() {
        return this.slotType == 'AO (4-20mA)';
    }

    get isAIType() {
        return (
            this.slotType == 'AI (1-5V)' ||
            this.slotType == 'AI (4-20mA)' ||
            this.slotType == 'AI (Pt100 2-wire)' ||
            this.slotType == 'AI (Pt100 3-wire)' ||
            this.slotType == 'AI (Thermal K)'
        );
    }

    get saveButtonText() {
        return this.translate.instant('system-settings.slot.button.save', {
            slotType: this.slotType,
        });
    }
}
